import React, {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useSelector, shallowEqual } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Backdrop, InputAdornment } from "@mui/material";
import { RiMapPin2Line } from "@remixicon/react";

/* ---------------------------------------------
   Constants
--------------------------------------------- */
const STORAGE_KEY = "store_id";

/* ---------------------------------------------
   Component
--------------------------------------------- */
const SearchSite = memo(function SearchSite() {
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [backdrop, setBackdrop] = useState(false);

    /* ---------------------------------------------
       Redux selector (optimized)
    --------------------------------------------- */
    const sites = useSelector(
        (state) => state.site.list || [],
        shallowEqual
    );

    /* ---------------------------------------------
       Memoized helpers
    --------------------------------------------- */
    const getOptionLabel = useCallback((option) => {
        return typeof option === "string" ? option : option?.name ?? "";
    }, []);

    const options = useMemo(() => sites, [sites]);

    /* ---------------------------------------------
       Restore / Default selection
    --------------------------------------------- */
    useEffect(() => {
        if (!sites.length) return;

        const storedId = localStorage.getItem(STORAGE_KEY);

        if (storedId) {
            const matchedSite = sites.find(
                (site) => site._id === storedId
            );

            if (matchedSite) {
                setValue(matchedSite);
                setInputValue(matchedSite.name);
                return;
            } else {
                localStorage.removeItem(STORAGE_KEY);
            }
        }

        // Fallback → first site
        const firstSite = sites[0];
        setValue(firstSite);
        setInputValue(firstSite.name);
        localStorage.setItem(STORAGE_KEY, firstSite._id);
    }, [sites]);

    /* ---------------------------------------------
       Event handlers (stable references)
    --------------------------------------------- */
    const handleChange = useCallback((_, newValue) => {
        if (newValue && typeof newValue === "object") {
            setValue(newValue);
            setInputValue(newValue.name);
            localStorage.setItem(STORAGE_KEY, newValue._id);
        } else {
            setValue(null);
            setInputValue(newValue || "");
            localStorage.removeItem(STORAGE_KEY);
        }
        setBackdrop(false);
    }, []);

    const handleInputChange = useCallback((_, newInputValue) => {
        setInputValue(newInputValue);
    }, []);

    const handleFocus = useCallback(() => {
        setBackdrop(true);
    }, []);

    const handleBlur = useCallback(() => {
        setBackdrop(false);
    }, []);

    /* ---------------------------------------------
       Render
    --------------------------------------------- */
    return (
        <>
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={backdrop}
            />

            <Autocomplete
                freeSolo
                blurOnSelect
                options={options}
                value={value}
                inputValue={inputValue}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={(option, val) =>
                    option?._id === val?._id
                }
                onChange={handleChange}
                onInputChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="filled"
                        size="small"
                        label="My Locations"
                        sx={{ minWidth: 300 }}
                        InputProps={{
                            ...params.InputProps,
                            type: "search",
                            endAdornment: (
                                <InputAdornment position="end">
                                    <RiMapPin2Line />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            />
        </>
    );
});

export default SearchSite;
